import { Account, AccountDto } from '@mkp/account/data-access';
import { DEFAULT_WALLET_NAME, WalletAccessRole, WalletPostBody } from '@mkp/shared/models';
import { LegalEntityDto } from '@mkp/legal-entity/data-access';
import { catchError, Observable, throwError } from 'rxjs';
import { OnboardingStep } from './onboarding-step.model';
import { LocationPostBody } from '@mkp/location/models';
import {
  CompanyProfileBase,
  CompanyProfileLink,
  CompanyProfileLinkType,
  CompanyProfilePostBody,
} from '@mkp/company/models';

// Body for creating an account membership via AccountMembershipResource.createAccountMembership
export function getAccountMembershipPostBody(account: AccountDto | Account) {
  const userId = '_embedded' in account ? account._embedded.createdBy.id : account.createdBy.id;
  return {
    userId,
    accountId: account.id,
  };
}

export function getWalletPostBody(accountId: AccountDto['id']): WalletPostBody {
  return {
    name: DEFAULT_WALLET_NAME,
    walletAccesses: [{ accountId, role: WalletAccessRole.Owner }],
  };
}

export function getCreateCompanyProfileBody(
  legalEntity: LegalEntityDto,
  companyWebsite?: CompanyProfileLink['url']
): CompanyProfilePostBody {
  return {
    legalEntityId: legalEntity.id,
    name: legalEntity.name,
    benefits: [],
    employeeCountFrom: legalEntity.employeeCountFrom || 1,
    employeeCountTo: legalEntity.employeeCountTo || 10,
    logoMediaApiId: null,
    headerMediaApiId: null,
    description: null,
    links: companyWebsite ? [{ url: companyWebsite, type: CompanyProfileLinkType.Website }] : null,
    videos: [],
    employerBranding: null,
  };
}

export function getLocationCreateBody(
  legalEntity: LegalEntityDto,
  companyProfile: Pick<CompanyProfileBase, 'id'>
): LocationPostBody {
  return {
    companyProfileId: companyProfile.id,
    address: {
      ...legalEntity.address,
      latitude: getCoordinate(legalEntity.address.latitude),
      longitude: getCoordinate(legalEntity.address.longitude),
    },
  };
}

export const getCoordinate = (coordinate: string | number | null | undefined): number | null =>
  !coordinate ? null : Number(coordinate);

function serializeOriginalError(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  return JSON.stringify(error);
}

export function getErrorMessage(error: any, stepName: OnboardingStep): string {
  const originalServerError =
    typeof error === 'string' ? error.match(/Original error:\s*(.*)/)?.[1] || error : error;
  const existingStep =
    typeof error === 'string'
      ? (error.match(/Step:\s*([^.]+?)\s*failed/) || [null, stepName])[1]
      : stepName;
  return `Claim Company Error, Step: ${existingStep} failed. Original error: ${serializeOriginalError(
    originalServerError
  )}`;
}

export function handleOnboardingError<T>(operation: OnboardingStep) {
  return (source$: Observable<T>): Observable<T> => {
    return source$.pipe(
      catchError((error) => {
        return throwError(() => getErrorMessage(error, operation));
      })
    );
  };
}
